// Turn pictures: a thing before and after a turn, or on its own. Plain React
// SVG, like practice-count.tsx. The only words are "Before" and "After"; how
// far and which way it turned is shown only by the drawings (and, when asked
// for, a dashed curved path).

import {
  describeTurnPicture,
  turnPicture,
  type TurnPictureSpec,
  type TurnThing,
} from './practice-turn-data';

const INK = '#2d4a3e';
const PANEL_FILL = '#f4f8f4';
const PATH_INK = '#5b7fa6';

// One drawing of each thing, facing up, in a square of side `size` centred
// on (0, 0). The view turns it to face the right way.
function Thing({ thing, size }: { thing: TurnThing; size: number }) {
  const r = size / 2;
  const line = {
    stroke: INK,
    strokeWidth: 2,
    strokeLinejoin: 'round' as const,
  };
  switch (thing) {
    case 'arrow':
      return (
        <polygon
          data-thing="arrow"
          points={[
            [0, -r],
            [r * 0.6, -r * 0.2],
            [r * 0.22, -r * 0.2],
            [r * 0.22, r],
            [-r * 0.22, r],
            [-r * 0.22, -r * 0.2],
            [-r * 0.6, -r * 0.2],
          ]
            .map(([x, y]) => `${String(x)},${String(y)}`)
            .join(' ')}
          fill="#f39c5a"
          {...line}
        />
      );
    case 'pencil':
      return (
        <g data-thing="pencil">
          <rect
            x={-r * 0.2}
            y={-r * 0.45}
            width={r * 0.4}
            height={r * 1.4}
            fill="#f2c94c"
            {...line}
          />
          <polygon
            points={`${String(-r * 0.2)},${String(-r * 0.45)} 0,${String(-r)} ${String(r * 0.2)},${String(-r * 0.45)}`}
            fill="#f6e3c0"
            {...line}
          />
          <polygon
            points={`${String(-r * 0.07)},${String(-r * 0.82)} 0,${String(-r)} ${String(r * 0.07)},${String(-r * 0.82)}`}
            fill={INK}
          />
          <rect
            x={-r * 0.2}
            y={r * 0.95}
            width={r * 0.4}
            height={r * 0.05}
            fill="#e98bbf"
            {...line}
          />
        </g>
      );
    case 'key':
      // The teeth are on one side only, so a key turned a half turn does not
      // look the same as one that has not turned.
      return (
        <g data-thing="key">
          <circle cx={0} cy={r * 0.62} r={r * 0.36} fill="#e8c766" {...line} />
          <circle cx={0} cy={r * 0.62} r={r * 0.12} fill="#ffffff" {...line} />
          <rect
            x={-r * 0.1}
            y={-r}
            width={r * 0.2}
            height={r * 1.28}
            fill="#e8c766"
            {...line}
          />
          <rect
            x={r * 0.1}
            y={-r * 0.9}
            width={r * 0.26}
            height={r * 0.16}
            fill="#e8c766"
            {...line}
          />
          <rect
            x={r * 0.1}
            y={-r * 0.56}
            width={r * 0.18}
            height={r * 0.16}
            fill="#e8c766"
            {...line}
          />
        </g>
      );
  }
}

export function TurnPictureView({ turn }: { turn: TurnPictureSpec }) {
  const picture = turnPicture(turn);
  const half = picture.panelSize / 2;
  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeTurnPicture(turn)}
        viewBox={`0 0 ${String(picture.width)} ${String(picture.height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.width * 1.3))}px` }}
      >
        {picture.panels.map((panel, index) => (
          <g key={index} data-panel={panel.label ?? 'only'}>
            <rect
              x={panel.cx - half}
              y={panel.cy - half}
              width={picture.panelSize}
              height={picture.panelSize}
              rx="12"
              fill={PANEL_FILL}
              stroke={INK}
              strokeWidth="1.5"
            />
            <g
              data-angle={panel.angle}
              transform={`translate(${String(panel.cx)} ${String(panel.cy)}) rotate(${String(panel.angle)})`}
            >
              <Thing thing={turn.thing} size={picture.thingSize} />
            </g>
            {panel.label !== null && (
              <text
                x={panel.cx}
                y={panel.cy + half + 20}
                textAnchor="middle"
                fill={INK}
                fontSize="16"
                fontWeight="600"
              >
                {panel.label}
              </text>
            )}
          </g>
        ))}
        {picture.path !== null && (
          // Dashed and thinner than the thing, so it is told from an arrow
          // without colour.
          <g data-path={picture.path.clockwise ? 'clockwise' : 'anticlockwise'}>
            <path
              d={picture.path.d}
              fill="none"
              stroke={PATH_INK}
              strokeWidth="3"
              strokeDasharray="7 5"
              strokeLinecap="round"
            />
            <polygon points={picture.path.head} fill={PATH_INK} />
          </g>
        )}
      </svg>
    </figure>
  );
}
